/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsx jsx */
import React, { Suspense, useState } from 'react'
import { css, jsx } from '@emotion/react'
import { Link } from 'react-router-dom'
import fuzzySearch from '@monorepo/shared/fuzzySearch.js'
import { useTranslation } from '@multilocale/react/index.js'
import IconBack from '@stiloso/icons/IconBack.js'
import IconClose from '@stiloso/icons/IconClose.js'
import SearchBar from '@stiloso/components/SearchBar.js'
import Spinner from '@stiloso/components/Spinner.js'
import useDocuments from '../useDocuments.js'

export const paths = ['/documents']

const list = css({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'scroll',
  overflowX: 'hidden',
})

const DocumentsPage = () => {
  const { t } = useTranslation()

  let { data: documents } = useDocuments()

  const [filteredDocuments, setFilteredDocuments] = useState(null)

  const onSearch = searchQuery => {
    if (searchQuery.length) {
      const filtered = fuzzySearch({
        query: searchQuery,
        list: documents,
        properties: ['title', 'content'],
      })
      setFilteredDocuments(filtered)
    } else {
      setFilteredDocuments(null)
    }
  }

  const onCancelSearch = () => {
    setFilteredDocuments(null)
  }

  return (
    <div
      css={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div css={{ flexGrow: 1, overflow: 'scroll' }}>
        {filteredDocuments?.length === 0 && (
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {t('Sorry, no results found')}
          </div>
        )}
        {documents?.length === 0 && (
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {t('No documents found')}
          </div>
        )}
        {filteredDocuments?.length > 0 && (
          <div css={list}>
            {filteredDocuments.map(filteredDocument => (
              <Document key={filteredDocument._id} {...filteredDocument} />
            ))}
          </div>
        )}
        {!filteredDocuments && documents.length > 0 && (
          <div css={list}>
            {documents.map(document => (
              <Document key={document._id} {...document} />
            ))}
          </div>
        )}
      </div>
      <div
        css={{
          display: 'flex',
          backgroundColor: 'white',
          borderTop: '2px solid var(--color-background)',
        }}
      >
        <SearchBar
          placeholder={t('Search documentation')}
          onSearch={onSearch}
          onCancel={onCancelSearch}
          inputStyles={{ color: 'var(--color-base)' }}
          styles={{ borderRadius: 0 }}
        />
      </div>
    </div>
  )
}

const Document = ({ _id, title }) => (
  <Link
    to={`/documents/${_id}`}
    css={[
      {
        position: 'relative',
        color: 'var(--color-base)',
        padding: 16,
        width: '100%',
        textAlign: 'left',
        alignSelf: 'flex-start',
        wordBreak: 'break-word',
        whiteSpace: 'pre-line',
        ':hover, :focus-visible': {
          background: 'var(--color-hover)',
          color: 'white',
        },
        ':active': {
          background: 'var(--color-active)',
          color: 'white',
        },
        borderBottom: '1px solid var(--color-background)',
      },
    ]}
  >
    {title}
  </Link>
)

const DocumentsPageWrapper = () => {
  const { t } = useTranslation()

  const closeWidget = () => {
    window?.parent?.postMessage({ type: 'closeChat' }, '*')
  }

  return (
    <div
      css={{
        background: 'white',
        borderRadius: 16,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        css={{
          display: 'flex',
          background: 'var(--color-primary)',
          color: 'white',
          fill: 'white',
          width: '100%',
          position: 'sticky',
          top: 0,
        }}
      >
        <div css={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div
            css={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={closeWidget}
              css={{
                background: 'transparent',
                border: 'none',
                padding: 16,
                cursor: 'pointer',
                ':hover, :focus-visible': {
                  background: 'var(--color-hover)',
                },
                ':active': {
                  background: 'var(--color-active)',
                },
                visibility: 'hidden',
              }}
            >
              <IconBack css={{ width: 24, height: 24 }} />
            </button>
            <Link
              css={{
                color: 'white',
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'center',
                cursor: 'pointer',
                height: 32,
                borderRadius: 24,
                ':hover, :focus-visible': {
                  background: 'var(--color-hover)',
                },
                ':active': {
                  background: 'var(--color-active)',
                },
              }}
              to="/messages"
            >
              {t('Chat')}
            </Link>
            <Link
              css={{
                color: 'white',
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'center',
                cursor: 'pointer',
                height: 32,
                borderRadius: 24,
                background: 'var(--color-primary-dark)',
                ':hover, :focus-visible': {
                  background: 'var(--color-hover)',
                },
                ':active': {
                  background: 'var(--color-active)',
                },
              }}
              to="/documents"
            >
              {t('Docs')}
            </Link>
            <button
              type="button"
              aria-label="Close"
              onClick={closeWidget}
              css={{
                background: 'transparent',
                border: 'none',
                padding: 16,
                cursor: 'pointer',
                ':hover, :focus-visible': {
                  background: 'var(--color-hover)',
                },
                ':active': {
                  background: 'var(--color-active)',
                },
              }}
            >
              <IconClose css={{ width: 24, height: 24 }} />
            </button>
          </div>
        </div>
      </div>
      <Suspense fallback={<Spinner />}>
        <DocumentsPage />
      </Suspense>
    </div>
  )
}

export default DocumentsPageWrapper
